import React from 'react'
import { connect, useSelector } from 'react-redux'

import { ApplicationState } from '@/redux/stores/types'
import { DefaultProps } from '@/travelsuit'
import { PermissionDefs, UserPermissionsTypes } from '@/types/permissions'

interface IProps extends DefaultProps {
	permissions: UserPermissionsTypes[] | Partial<PermissionDefs>
	userPermissions: UserPermissionsTypes[]
	fallback?: React.ReactNode
}

const isDef = (def: Partial<PermissionDefs> | UserPermissionsTypes[]): def is Partial<PermissionDefs> => {
	return !(def instanceof Array)
}

const asDef = (def: Partial<PermissionDefs> | UserPermissionsTypes[]): PermissionDefs => {
	const fixedDef: PermissionDefs = !isDef(def)
		? { every: [], oneOf: def, either: false }
		: { every: def.every || [], oneOf: def.oneOf || [], either: def.either || false }

	return fixedDef
}

const permissionChecker = (
	def: Partial<PermissionDefs> | UserPermissionsTypes[],
	userPermissions: UserPermissionsTypes[] = [],
) => {
	const fixedDef = asDef(def)
	const every = !fixedDef.every.length || fixedDef.every.every((d) => userPermissions.includes(d))
	const oneOf = !fixedDef.oneOf.length || fixedDef.oneOf.some((d) => userPermissions.includes(d))
	return fixedDef.either ? every || oneOf : every && oneOf
}

const CheckUserPermission: React.FunctionComponent<IProps> = (props) => {
	const { permissions, children, userPermissions, fallback } = props

	return userPermissions.length ? <>{permissionChecker(permissions, userPermissions) ? children : fallback}</> : null
}

const mapStateToProps = ({ userPermissions }: ApplicationState) => ({
	userPermissions,
})

export default connect(mapStateToProps)(CheckUserPermission)

export const useCheckUserPermission = () => {
	const userPermissions = useSelector((state: ApplicationState) => state.userPermissions)

	return (permissions: UserPermissionsTypes[] | Partial<PermissionDefs>) => {
		return !!userPermissions.length && permissionChecker(permissions, userPermissions)
	}
}
